import React, { useCallback, useRef, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { API } from '../../../lib/api';
import { Label } from '../../../components/ui/label';

export const STEPS = [
  { key: 'keywords', title: 'Keyword Research', blurb: 'Enter the topic you want to write about. AI will research the best keywords to target for the executive briefing & events niche.' },
  { key: 'intent', title: 'Search Intent', blurb: 'Understand what the searcher actually wants so the article answers it directly.' },
  { key: 'research', title: 'Research', blurb: 'Build the outline, key facts and sources before writing a single paragraph.' },
  { key: 'draft', title: 'First Draft', blurb: 'AI writes the complete first draft from your keywords, intent and outline. You can edit it right here.' },
  { key: 'expert', title: 'BriefingIQ Expert Inputs', blurb: 'Add the BriefingIQ point of view — practitioner insight and product angles — then weave them in.' },
  { key: 'facts', title: 'Factual Verification', blurb: 'Every statistic, date and absolute statement gets flagged for a human to verify.' },
  { key: 'original', title: 'Original BriefingIQ Information', blurb: 'Add proprietary data, customer stories and quotes only BriefingIQ can publish.' },
  { key: 'editing', title: 'Human Editing', blurb: 'The content team edits the article by hand. Optional AI polish keeps your voice intact.' },
  { key: 'seo', title: 'SEO Optimization', blurb: 'Meta title, description, slug, tags and an on-page checklist against the primary keyword.' },
  { key: 'jsonld', title: 'JSON-LD', blurb: 'Structured data preview. BlogPosting is added automatically; add FAQ schema for rich results.' },
  { key: 'publish', title: 'Publish', blurb: 'Add the cover image, do a final review, then publish now or save as a draft post.' },
];

export const Field = ({ label, hint, children, required, testId }) => (
  <div data-testid={testId}>
    <Label className="text-[11px] uppercase tracking-wider text-ink-3">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
    {hint && <div className="text-[10px] text-ink-3 mt-1">{hint}</div>}
  </div>
);

export const AiButton = ({ onClick, running, label, runningLabel = 'Working…', secondary, testId, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={running || disabled}
    data-testid={testId}
    className={`${secondary ? 'btn-secondary' : 'btn-primary'} disabled:opacity-60`}
  >
    {running ? (
      <><Loader2 className="w-4 h-4 animate-spin" /> {runningLabel}</>
    ) : (
      <><Sparkles className="w-4 h-4" /> {label}</>
    )}
  </button>
);

export const StreamPreview = ({ text, label = 'AI is writing…' }) => (
  <div className="rounded-xl border border-[var(--line-2)] bg-[var(--bg-alt)] p-4" data-testid="ai-stream-preview">
    <div className="text-[11px] uppercase tracking-wider text-ink-3 mb-2 inline-flex items-center gap-1.5">
      <Loader2 className="w-3 h-3 animate-spin" /> {label}
    </div>
    <pre className="whitespace-pre-wrap font-mono text-xs text-ink-2 max-h-64 overflow-auto">{text}</pre>
  </div>
);

export const inputCls = 'rounded-lg border-[var(--line-2)]';
export const wordCount = (s) => (s || '').trim().split(/\s+/).filter(Boolean).length;
export const splitLines = (s) => (s || '').split('\n').map((l) => l.trim()).filter(Boolean);

// Streams an AI action via SSE. Returns { run, running, action, text }.
export const useAiStream = () => {
  const [running, setRunning] = useState(false);
  const [action, setAction] = useState(null);
  const [text, setText] = useState('');
  const abortRef = useRef(null);

  const run = useCallback(async (workflowId, act, input = {}, onDelta) => {
    setRunning(true);
    setAction(act);
    setText('');
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`${API}/admin/workflows/${workflowId}/ai/${act}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({ input }),
        signal: ctrl.signal,
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch (e) { /* ignore */ }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';
      let result = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === 'delta') {
            acc += ev.text;
            setText(acc);
            onDelta?.(acc);
          } else if (ev.type === 'error') {
            throw new Error(ev.detail);
          } else if (ev.type === 'done') {
            result = ev.result;
          }
        }
      }
      if (result === null) throw new Error('The AI stream ended without a result.');
      return result;
    } finally {
      setRunning(false);
      setAction(null);
    }
  }, []);

  return { run, running, action, text };
};
